import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Instagram, Send } from 'lucide-react';

const WHATSAPP_NUMBER = "5521980277322";

const contactInfo = [
  {
    icon: Phone,
    label: "WhatsApp",
    value: "(21) 98027-7322",
    link: `https://wa.me/${WHATSAPP_NUMBER}`,
  },
  {
    icon: Mail,
    label: "E-mail",
    value: "vaztechbarra@gmail.com",
    link: "mailto:vaztechbarra@gmail.com",
  },
  {
    icon: MapPin,
    label: "Endereço",
    value: "Av. Embaixador Abelardo Bueno, 1340 - Barra da Tijuca, RJ",
    link: "https://maps.google.com/?q=Av.+Embaixador+Abelardo+Bueno,+1340+-+Barra+da+Tijuca,+RJ",
  },
  {
    icon: Clock,
    label: "Horário",
    value: "Seg-Sex: 9h-18h | Sáb: 9h-14h",
    link: null,
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@vaztech_barra",
    link: "https://instagram.com/vaztech_barra",
  },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    device: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `Olá! Vim pelo site e gostaria de um orçamento.

*Nome:* ${formData.name}
*Telefone:* ${formData.phone}
*Dispositivo:* ${formData.device}
*Mensagem:* ${formData.message}`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contato" className="section-darker py-24 md:py-32 grid-bg" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest">
            [ Contato ]
          </span>
          <h2 className="font-orbitron text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
            Fale <span className="text-[#00F0FF]">Conosco</span>
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto font-space">
            Entre em contato para um orçamento gratuito. Estamos prontos para atender você!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-mono text-[#00F0FF] mb-2">Nome *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Seu nome completo"
                    className="w-full"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[#00F0FF] mb-2">Telefone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(21) 99999-9999"
                    className="w-full"
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-mono text-[#00F0FF] mb-2">Dispositivo</label>
                <input
                  type="text"
                  name="device"
                  value={formData.device}
                  onChange={handleChange}
                  placeholder="Ex: iPhone 14, Notebook Dell, Apple Watch"
                  className="w-full"
                  data-testid="contact-input-device"
                />
              </div>

              <div>
                <label className="block text-sm font-mono text-[#00F0FF] mb-2">Mensagem *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Descreva o problema ou dúvida..."
                  className="w-full resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <button
                type="submit"
                className="whatsapp-btn w-full justify-center"
                data-testid="contact-submit-btn"
              >
                <Send size={18} />
                Enviar pelo WhatsApp
              </button>
            </form>

            {/* Contact Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {contactInfo.slice(0, 4).map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="cyber-card p-4"
                >
                  {info.link ? (
                    <a
                      href={info.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                      data-testid={`contact-info-${index}`}
                    >
                      <info.icon size={20} className="text-[#00F0FF] flex-shrink-0 mt-1" />
                      <div>
                        <div className="text-xs font-mono text-[#00F0FF] mb-1">{info.label}</div>
                        <div className="text-sm text-[#94A3B8] group-hover:text-white transition-colors">
                          {info.value}
                        </div>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-start gap-3">
                      <info.icon size={20} className="text-[#00F0FF] flex-shrink-0 mt-1" />
                      <div>
                        <div className="text-xs font-mono text-[#00F0FF] mb-1">{info.label}</div>
                        <div className="text-sm text-[#94A3B8]">{info.value}</div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="map-container h-full min-h-[400px] lg:min-h-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3673.0901234567!2d-43.3652!3d-22.9983!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9bda2dd94e7b95%3A0x0!2sAv.%20Embaixador%20Abelardo%20Bueno%2C%201340%20-%20Barra%20da%20Tijuca%2C%20Rio%20de%20Janeiro%20-%20RJ!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Vaz Tech Solutions"
                data-testid="contact-map"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
